import { BigNum, PERCENTAGE_PRECISION_EXP } from '@velocity-exchange/sdk';
import { MarketId } from '@velocity-exchange/common';
import React from 'react';
import SkeletonValuePlaceholder from 'src/components/SkeletonValuePlaceholder/SkeletonValuePlaceholder';
import NumberDisplay from '../NumberDisplay/NumberDisplay';
import use24hMarketDataFromStore from 'src/hooks/use24hMarketDataFromStore';
import { cj } from 'src/utils/classNames';
import Tag from 'src/components/primitives/Tag/Tag';

interface Props {
	marketId: MarketId | undefined;
	markPrice: BigNum;
	className?: string;
	withBackground?: boolean;
}

const PriceChangePercentDisplay_ = ({
	marketId,
	markPrice,
	className,
	withBackground,
}: Props) => {
	const dataForMarket = use24hMarketDataFromStore({
		targetMarketType: marketId?.marketType ?? 'perp',
		targetMarketIndex: marketId?.marketIndex ?? 0,
	})[0];

	if (!marketId) {
		return <Skeleton />;
	}

	const pricePercentChange = (() => {
		if (!dataForMarket?.price24hAgo || !markPrice || markPrice.eqZero()) {
			if (dataForMarket?.pricePercentChange) {
				return dataForMarket.pricePercentChange;
			}

			return;
		}

		const price24hAgo = dataForMarket.price24hAgo;

		return 100 * ((markPrice.toNum() - price24hAgo) / price24hAgo);
	})();

	if (pricePercentChange === undefined || isNaN(pricePercentChange)) {
		return <Skeleton />;
	}

	const backgroundClassName = (() => {
		if (!withBackground) {
			return;
		}

		if (pricePercentChange > 0) {
			return 'bg-positive-green-secondary-bg';
		}

		return 'bg-negative-red-secondary-bg';
	})();

	return (
		<Tag className={cj(backgroundClassName, className)}>
			<NumberDisplay
				value={BigNum.fromPrint(
					(pricePercentChange ?? 0).toString(),
					PERCENTAGE_PRECISION_EXP
				)}
				type="percentage_signed"
				signDependentColor
			/>
		</Tag>
	);
};

function Skeleton() {
	return (
		<SkeletonValuePlaceholder
			className="my-px xs:h-[11px] sm:h-[20px]"
			loading
		/>
	);
}

export const PriceChangePercentDisplay = React.memo(PriceChangePercentDisplay_);
